export function initNotifications() {
    const unreadCountBadge = document.getElementById('notification-unread-count');
    const notificationContainer = document.getElementById('notification-list-container');
    const notificationsDropdownToggle = document.getElementById('page-header-notifications-dropdown');
    const notificationBellIcon = document.getElementById('notification-bell-icon');

    if (!unreadCountBadge || !notificationContainer || !notificationsDropdownToggle || !notificationBellIcon) {
        return;
    }

    // the bell rings until the list is opened, and again only when something newer arrives
    const SEEN_KEY = 'ms_notifications_seen_id';
    let latestNotificationId = 0;

    const getSeenId = () => {
        try {
            return parseInt(localStorage.getItem(SEEN_KEY), 10) || 0;
        } catch {
            return 0;
        }
    };

    const updateBellMotion = (unreadCount) => {
        notificationBellIcon.classList.toggle('bx-tada', unreadCount > 0 && latestNotificationId > getSeenId());
    };

    notificationsDropdownToggle.addEventListener('show.bs.dropdown', () => {
        notificationBellIcon.classList.remove('bx-tada');
        try {
            localStorage.setItem(SEEN_KEY, String(Math.max(latestNotificationId, getSeenId())));
        } catch {
            // it stops for this page view anyway
        }
    });

    const getLabels = () => {
        return {
            loading: notificationContainer.getAttribute('data-label-loading') || 'Loading...',
            empty: notificationContainer.getAttribute('data-label-empty') || 'No new notifications.',
            error: notificationContainer.getAttribute('data-label-error') || 'Error loading notifications.',
            justNow: notificationContainer.getAttribute('data-label-just-now') || 'just now',
            markRead: notificationContainer.getAttribute('data-label-mark-read') || 'Mark as Read'
        };
    };

    const getRelativeTimeFormatter = () => {
        const lang = document.documentElement.getAttribute('lang') || 'en';
        try {
            return new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
        } catch {
            return null;
        }
    };

    const timeSince = (date) => {
        if (!date || isNaN(date.getTime()) || date.getFullYear() < 2000) {
            return getLabels().justNow;
        }
        const now = new Date();
        const diffSeconds = Math.round((date - now) / 1000);
        const absSeconds = Math.abs(diffSeconds);
        const labels = getLabels();

        if (absSeconds < 45) {
            return labels.justNow;
        }

        const rtf = getRelativeTimeFormatter();
        if (rtf) {
            if (absSeconds < 3600) {
                return rtf.format(Math.round(diffSeconds / 60), 'minute');
            }
            if (absSeconds < 86400) {
                return rtf.format(Math.round(diffSeconds / 3600), 'hour');
            }
            if (absSeconds < 2592000) {
                return rtf.format(Math.round(diffSeconds / 86400), 'day');
            }
            if (absSeconds < 31536000) {
                return rtf.format(Math.round(diffSeconds / 2592000), 'month');
            }
            return rtf.format(Math.round(diffSeconds / 31536000), 'year');
        }

        const minutes = Math.floor(absSeconds / 60);
        return `${minutes}m ago`;
    };

    const sanitizeHtml = (str) => {
        if (!str) return '';
        const temp = document.createElement('div');
        temp.textContent = str;
        return temp.innerHTML;
    };

    const fetchNotifications = async () => {
        try {
            const response = await fetch('/UserNotification/GetRecent?limit=5');
            if (!response.ok) {
                console.error('Failed to fetch notifications');
                return;
            }

            const data = await response.json();
            updateNotificationsUI(data.notifications || [], data.unreadCount || 0);
        } catch (error) {
            console.error('Error fetching notifications:', error);
            const labels = getLabels();
            notificationContainer.innerHTML = `<div class="text-center p-3 text-muted small">${sanitizeHtml(labels.error)}</div>`;
        }
    };

    const updateNotificationsUI = (notifications, unreadCount) => {
        const labels = getLabels();

        latestNotificationId = notifications.reduce((max, n) => Math.max(max, Number(n.notificationId) || 0), 0);

        // Update badge
        if (unreadCount > 0) {
            unreadCountBadge.textContent = unreadCount;
            unreadCountBadge.style.display = 'inline-block';
        } else {
            unreadCountBadge.style.display = 'none';
        }
        updateBellMotion(unreadCount);

        // Update list
        if (notifications.length === 0) {
            notificationContainer.innerHTML = `
                <div class="text-center p-4 text-muted small">
                    <i class="bx bx-check-double mb-2 font-size-24"></i>
                    <br/>${sanitizeHtml(labels.empty)}
                </div>`;
            return;
        }

        const normalizeIcon = (icon) => {
            if (!icon) return 'bx bx-bell';
            if (icon === 'mdi mdi-hand-wave' || icon === 'bx bx-party') return 'bx bx-smile';
            if (icon === 'bx bx-category') return 'bx bx-grid-alt';
            if (icon === 'bx bx-purchase-tag') return 'bx bx-purchase-tag-alt';
            return icon;
        };

        const html = notifications.map(notification => {
            const rawDate = notification.createdDate || notification.CreatedDate;
            const timeAgo = rawDate ? sanitizeHtml(timeSince(new Date(rawDate))) : sanitizeHtml(labels.justNow);
            const resolvedIcon = normalizeIcon(notification.icon);
            const iconHtml = `<i class="${sanitizeHtml(resolvedIcon)}"></i>`;

            const bgClass = notification.type === 'Success' ? 'bg-success' :
                notification.type === 'Warning' ? 'bg-warning' :
                    notification.type === 'Danger' ? 'bg-danger' :
                        notification.type === 'Info' ? 'bg-info' : 'bg-primary';

            const itemClass = notification.isRead ? 'text-reset notification-item d-block' : 'text-reset notification-item d-block bg-light bg-opacity-50';
            const targetUrl = notification.linkUrl || notification.actionUrl || 'javascript:void(0);';
            const isModal = targetUrl.includes('DetailsModal');
            const modalAttrs = isModal ? `data-toggle="ajax-modal" data-target="#edit_modal" data-url="${sanitizeHtml(targetUrl)}"` : '';
            const hrefAttr = isModal ? 'href="javascript:void(0);"' : `href="${sanitizeHtml(targetUrl)}"`;

            return `
                <a ${hrefAttr} 
                   ${modalAttrs}
                   class="${itemClass}" 
                   data-id="${notification.notificationId}"
                   data-is-read="${notification.isRead}">
                    <div class="d-flex align-items-start">
                        <div class="avatar-xs me-3 flex-shrink-0">
                            <span class="avatar-title ${bgClass} rounded-circle font-size-16" style="width: 2rem; height: 2rem; min-width: 2rem;">
                                ${iconHtml}
                            </span>
                        </div>
                        <div class="flex-grow-1" style="min-width: 0; word-break: break-word;">
                            <h6 class="mb-1 font-size-14 fw-semibold text-truncate text-body" title="${sanitizeHtml(notification.title)}">${sanitizeHtml(notification.title)}</h6>
                            <div class="font-size-12 text-muted">
                                <p class="mb-1" style="white-space: normal; line-height: 1.4;">${sanitizeHtml(notification.message)}</p>
                                <p class="mb-0 text-muted small"><i class="mdi mdi-clock-outline me-1"></i><span>${timeAgo}</span></p>
                            </div>
                        </div>
                    </div>
                </a>
            `;
        }).join('');

        notificationContainer.innerHTML = html;

        // Add event listeners for marking as read
        const items = notificationContainer.querySelectorAll('.notification-item');
        items.forEach(item => {
            item.addEventListener('click', async () => {
                const id = item.getAttribute('data-id');
                const isRead = item.getAttribute('data-is-read') === 'true';

                if (!isRead && id) {
                    try {
                        const tokenInput = document.querySelector('input[name="__RequestVerificationToken"]');
                        const token = tokenInput ? tokenInput.value : '';
                        const formData = new URLSearchParams();
                        formData.append('id', id);
                        if (token) formData.append('__RequestVerificationToken', token);

                        await fetch('/UserNotification/MarkAsRead', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/x-www-form-urlencoded',
                                'RequestVerificationToken': token
                            },
                            body: formData.toString()
                        });

                        item.classList.remove('bg-light');
                        item.setAttribute('data-is-read', 'true');

                        let currentCount = parseInt(unreadCountBadge.textContent) || 0;
                        if (currentCount > 0) {
                            currentCount--;
                            unreadCountBadge.textContent = currentCount;
                            if (currentCount === 0) {
                                unreadCountBadge.style.display = 'none';
                                notificationBellIcon.classList.remove('bx-tada');
                            }
                        }
                    } catch (error) {
                        console.error('Error marking as read', error);
                    }
                }
            });
        });
    };

    const topbarMarkAllBtn = document.getElementById('topbar-mark-all-read-btn');
    if (topbarMarkAllBtn) {
        topbarMarkAllBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
                const tokenInput = document.querySelector('input[name="__RequestVerificationToken"]');
                const token = tokenInput ? tokenInput.value : '';
                const formData = new URLSearchParams();
                if (token) formData.append('__RequestVerificationToken', token);

                const res = await fetch('/UserNotification/MarkAllAsRead', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'RequestVerificationToken': token,
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: formData.toString()
                });

                if (res.ok) {
                    unreadCountBadge.style.display = 'none';
                    notificationBellIcon.classList.remove('bx-tada');
                    const items = notificationContainer.querySelectorAll('.notification-item');
                    items.forEach(item => {
                        item.classList.remove('bg-light', 'bg-opacity-50');
                        item.setAttribute('data-is-read', 'true');
                    });

                    // If on /UserNotification page with active tab container, refresh its content smoothly
                    const tabContainer = document.getElementById('notificationsTabContent');
                    if (tabContainer && window.location.pathname.toLowerCase().includes('usernotification')) {
                        const evt = new CustomEvent('notifications:allRead');
                        document.dispatchEvent(evt);
                    }
                }
            } catch (err) {
                console.error('Error marking all notifications as read from topbar:', err);
            }
        });
    }

    fetchNotifications();
    notificationsDropdownToggle.addEventListener('show.bs.dropdown', fetchNotifications);
}

